// Size guide tabs — implements the WAI-ARIA Tab Pattern (roving tabindex,
// Left/Right/Home/End navigation) per .github/skills/tab-accessibility/SKILL.md
//
// Theme change: HUSHOP-137. The gender panels can each nest an
// independent category tablist, so tabs/panels are scoped per `role="tablist"`
// group (via `aria-controls`) instead of queried globally across the whole
// element; otherwise a click or arrow key in one tablist would also move focus
// and toggle `hidden` on the other tablist's tabs/panels.
class NovicellSizeGuideTabs extends HTMLElement {
  #controller = new AbortController();

  /** @returns {HTMLElement[]} */
  get #tablists() {
    return Array.from(this.querySelectorAll('[role="tablist"]'));
  }

  connectedCallback() {
    const { signal } = this.#controller;

    this.#tablists.forEach((tablist) => {
      this.#tabsFor(tablist).forEach((tab) => {
        tab.addEventListener('click', () => this.#activate(tablist, tab), { signal });
      });

      tablist.addEventListener('keydown', this.#handleKeydown, { signal });
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * @param {HTMLElement} tablist
   * @returns {HTMLElement[]}
   */
  #tabsFor(tablist) {
    return Array.from(tablist.querySelectorAll(':scope > [role="tab"]'));
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.getAttribute('role') !== 'tab') return;

    const tablist = target.closest('[role="tablist"]');
    if (!tablist) return;

    const tabs = this.#tabsFor(tablist);
    const index = tabs.indexOf(target);
    if (index === -1) return;

    const navMap = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -index,
      End: tabs.length - 1 - index,
    };

    const offset = navMap[/** @type {keyof typeof navMap} */ (event.key)];
    if (offset === undefined) return;

    event.preventDefault();

    const nextIndex = (index + offset + tabs.length) % tabs.length;
    const nextTab = tabs[nextIndex];
    if (!nextTab) return;

    this.#activate(tablist, nextTab);
    nextTab.focus();
  };

  /**
   * @param {HTMLElement} tablist
   * @param {HTMLElement} tab
   */
  #activate(tablist, tab) {
    const panelId = tab.getAttribute('aria-controls');
    if (!panelId) return;

    this.#tabsFor(tablist).forEach((otherTab) => {
      const isSelected = otherTab === tab;

      otherTab.setAttribute('aria-selected', String(isSelected));
      otherTab.setAttribute('tabindex', isSelected ? '0' : '-1');
    });

    const panel = this.querySelector(`#${CSS.escape(panelId)}`);
    const panelGroup = panel?.parentElement;
    if (!panelGroup) return;

    Array.from(panelGroup.children).forEach((sibling) => {
      if (!(sibling instanceof HTMLElement) || sibling.getAttribute('role') !== 'tabpanel') return;
      sibling.toggleAttribute('hidden', sibling.id !== panelId);
    });
  }
}

if (!customElements.get('novicell-size-guide-tabs')) {
  customElements.define('novicell-size-guide-tabs', NovicellSizeGuideTabs);
}
